import React, { useEffect, useState } from "react";
import { useNavigate, Navigate } from "react-router-dom";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { useApp } from "@/context/AppContext";
import { t } from "@/lib/i18n";
import Header from "@/components/Header";
import StepIndicator from "@/components/StepIndicator";
import { Button } from "@/components/ui/button";
import api from "@/lib/api";
import { Copy, ExternalLink, ArrowLeft, ArrowRight, Save, CheckCircle2, Paperclip, FileText, Image as ImageIcon } from "lucide-react";

const SubmitPage = () => {
  const { language, category, originalText, refinedText, attachments, user } = useApp();
  const navigate = useNavigate();
  const [info, setInfo] = useState(null);
  const [saved, setSaved] = useState(false);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    if (!language || !category || !refinedText) return;
    api.get(`/submission-info/${category}?language=${language}`).then((r) => setInfo(r.data));
  }, [language, category, refinedText]);

  if (!language) return <Navigate to="/" replace />;
  if (!category || !refinedText) return <Navigate to="/complaint" replace />;

  const Arrow = language === "ar" ? ArrowLeft : ArrowRight;
  const BackArrow = language === "ar" ? ArrowRight : ArrowLeft;

  const copy = async () => {
    await navigator.clipboard.writeText(refinedText);
    setCopied(true);
    toast.success(t(language, "copied"));
    setTimeout(() => setCopied(false), 2000);
  };

  const save = async () => {
    try {
      await api.post("/complaints", {
        category, language, original_text: originalText, refined_text: refinedText,
        attachment_ids: attachments.map((a) => a.id),
      });
      setSaved(true);
      toast.success(t(language, "saved"));
    } catch (e) {
      toast.error(e?.response?.data?.detail || t(language, "errorGeneric"));
    }
  };

  return (
    <div className="min-h-screen bg-[#F8FAFC]">
      <Header />
      <main className="max-w-5xl mx-auto px-6 py-10">
        <StepIndicator current={4} />
        <motion.div
          initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.4 }}
          className="mt-8 mb-8 text-start"
        >
          <h1 className="text-3xl sm:text-4xl font-bold text-slate-900 tracking-tight mb-3">
            {t(language, "submitTitle")}
          </h1>
          <p className="text-slate-500 text-base">{t(language, "submitSub")}</p>
        </motion.div>

        <div className="grid grid-cols-1 lg:grid-cols-5 gap-6">
          <div className="lg:col-span-3 bg-white border border-slate-200 rounded-2xl p-6">
            <h2 className="text-lg font-semibold text-slate-900 mb-4 text-start">
              {info?.authority?.[language === "ar" ? "name_ar" : "name_en"] || "…"}
            </h2>
            <ol className="space-y-3">
              {info?.steps?.map((step, i) => (
                <li key={i} className="flex gap-3 text-start" data-testid={`submit-step-${i}`}>
                  <div className="flex-shrink-0 h-7 w-7 rounded-full bg-[#006C35]/10 text-[#006C35] flex items-center justify-center text-sm font-semibold">
                    {i + 1}
                  </div>
                  <p className="text-sm text-slate-700 leading-relaxed pt-1">{step}</p>
                </li>
              ))}
            </ol>

            <a
              data-testid="submit-open-portal-btn"
              href={info?.authority?.portal}
              target="_blank"
              rel="noopener noreferrer"
              className="mt-6 inline-flex items-center gap-2 h-11 px-5 rounded-md bg-[#006C35] hover:bg-[#004F27] text-white text-sm font-medium transition-colors"
            >
              <ExternalLink className="h-4 w-4" />
              {t(language, "openPortal")} — {info?.authority?.portal?.replace("https://", "")}
            </a>
          </div>

          <div className="lg:col-span-2 space-y-4">
            <div className="bg-white border border-slate-200 rounded-2xl p-6">
              <div className="flex items-center justify-between mb-3">
                <h3 className="text-sm font-semibold text-slate-900 text-start">{t(language, "yourDraft")}</h3>
                <Button
                  data-testid="submit-copy-btn"
                  size="sm"
                  variant="outline"
                  onClick={copy}
                  className="h-8 text-xs"
                >
                  {copied ? <CheckCircle2 className="h-3.5 w-3.5 me-1.5 text-[#006C35]" /> : <Copy className="h-3.5 w-3.5 me-1.5" />}
                  {t(language, "copyText")}
                </Button>
              </div>
              <div
                data-testid="submit-draft-text"
                className="text-sm text-slate-700 bg-slate-50 border border-slate-200 rounded-lg p-4 max-h-72 overflow-auto whitespace-pre-wrap leading-relaxed text-start"
                dir={language === "ar" ? "rtl" : "ltr"}
              >
                {refinedText}
              </div>
            </div>

            {attachments.length > 0 && (
              <div className="bg-white border border-slate-200 rounded-2xl p-6" data-testid="submit-attachments">
                <div className="flex items-center gap-2 mb-3">
                  <Paperclip className="h-4 w-4 text-slate-500" />
                  <h3 className="text-sm font-semibold text-slate-900 text-start">
                    {t(language, "attachedFiles")} ({attachments.length})
                  </h3>
                </div>
                <ul className="space-y-2">
                  {attachments.map((a) => {
                    const isImage = a.content_type?.startsWith("image/");
                    const Icon = isImage ? ImageIcon : FileText;
                    return (
                      <li key={a.id} className="flex items-center gap-3 text-sm text-slate-700">
                        <Icon className="h-4 w-4 text-[#006C35] flex-shrink-0" />
                        <span className="truncate text-start">{a.filename}</span>
                      </li>
                    );
                  })}
                </ul>
                <p className="text-xs text-slate-500 mt-3 leading-relaxed text-start">
                  {language === "ar"
                    ? "لا يمكن رفع المرفقات تلقائيًا إلى البوابة الرسمية. حمّلها واستخدمها يدويًا عند التقديم."
                    : "The official portal cannot receive attachments automatically. Keep them ready to upload manually while submitting."}
                </p>
              </div>
            )}

            {user && (
              <Button
                data-testid="submit-save-btn"
                variant="outline"
                onClick={save}
                disabled={saved}
                className="w-full h-11 border-[#006C35]/30 text-[#006C35] hover:bg-[#006C35] hover:text-white"
              >
                {saved ? <CheckCircle2 className="h-4 w-4 me-2" /> : <Save className="h-4 w-4 me-2" />}
                {saved ? t(language, "saved") : t(language, "saveToAccount")}
              </Button>
            )}
          </div>
        </div>

        <div className="flex items-center justify-between mt-8 gap-3">
          <Button
            data-testid="submit-back-btn"
            variant="outline"
            onClick={() => navigate("/complaint")}
            className="h-11"
          >
            <BackArrow className="h-4 w-4 me-2" />{t(language, "back")}
          </Button>
          <Button
            data-testid="submit-next-btn"
            onClick={() => navigate("/policies")}
            className="h-11 bg-[#006C35] hover:bg-[#004F27] text-white px-6"
          >
            {t(language, "next")}<Arrow className="h-4 w-4 ms-2" />
          </Button>
        </div>
      </main>
    </div>
  );
};

export default SubmitPage;
